import React, { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { IMGS } from '../../data/constants';

export function Hero() {
  const container = useRef(null);

  useGSAP(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    tl.from('.hero-eyebrow', { y: 20, opacity: 0, duration: 0.8, delay: 0.2 })
      .from('.hero-title', { y: 30, opacity: 0, duration: 0.9 }, '-=0.4')
      .from('.hero-desc', { y: 20, opacity: 0, duration: 0.8 }, '-=0.5')
      .from('.hero-btns', { y: 20, opacity: 0, duration: 0.8 }, '-=0.4')
      .from('.hero-scroll', { opacity: 0, duration: 0.8 }, '-=0.2');
  }, { scope: container });

  return (
    <section id="hero" ref={container} className="relative h-screen min-h-[700px] flex items-end overflow-hidden">
      {/* BG gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0d3526] via-nestlent-green via-[40%] to-[#1a2520]" />

      {/* Background images */}
      <div className="absolute inset-0 grid grid-cols-3 opacity-35">
        {[IMGS.i53, IMGS.i21, IMGS.i54].map((src, i) => (
          <div key={i} className="overflow-hidden">
            <img
              src={src}
              alt=""
              loading="eager"
              fetchPriority="high"
              className={`w-full h-full object-cover saturate-[0.6] brightness-[0.7] scale-105 hero-zoom ${i === 1 ? 'hero-zoom-delay-1' : i === 2 ? 'hero-zoom-delay-2' : ''}`}
            />
          </div>
        ))}
      </div>

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,20,15,0.85)] via-[rgba(10,20,15,0.3)] to-[rgba(10,20,15,0.1)]" />

      {/* Content */}
      <div className="relative z-[2] px-8 md:px-16 pb-28 max-w-[900px]">
        <div className="hero-eyebrow text-[0.72rem] font-normal tracking-[0.35em] uppercase text-nestlent-green-mid mb-5">
          Where Happiness Resides · Builders & Developers
        </div>
        <h1 className="hero-title font-serif text-[clamp(3.2rem,7vw,6rem)] font-normal leading-[1.06] text-white mb-7">
          Make Your Dream<br/>A <em className="font-serif italic text-nestlent-green-mid">Reality</em>
        </h1>
        <p className="hero-desc text-base font-light leading-[1.7] text-white/70 max-w-[540px] mb-11">
          With our unparalleled expertise, commitment to quality, and dedicated team. We deliver outstanding craftsmanship and exceptional service — creating harmonious spaces through our Vastu-integrated green building techniques.
        </p>
        <div className="hero-btns flex gap-4 flex-wrap">
          <a href="#projects" className="inline-flex items-center gap-2.5 text-[0.78rem] font-medium tracking-[0.12em] uppercase text-white bg-nestlent-green px-8 py-3.5 rounded-sm no-underline transition-all duration-300 hover:bg-nestlent-green-light hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(26,107,74,0.4)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            View Projects
          </a>
          <a href="tel:+919947847587" className="inline-flex items-center gap-2.5 text-[0.78rem] font-medium tracking-[0.12em] uppercase text-white bg-transparent border border-white/35 px-8 py-3.5 rounded-sm no-underline transition-all duration-300 hover:bg-white/10 hover:border-white/60">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
            Call Now
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="hero-scroll absolute right-8 md:right-16 bottom-28 z-[2] flex items-center gap-4">
        <div className="w-[50px] h-px bg-white/30" />
        <div className="text-[0.65rem] tracking-[0.25em] uppercase text-white/50 [writing-mode:vertical-rl] rotate-180">
          Scroll
        </div>
      </div>
    </section>
  );
}
